import { FC, KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import type { WorkOrderStatus } from "@myapp/contract";
import { useOutsideClick } from "../../../shared/hooks/useOutsideClick";
import {
  getWorkOrderStatusColor,
  getWorkOrderStatusLabel,
} from "../../../entities/work-order";
import { STATUS_GROUPS } from "../model/statusGroups";

interface Props {
  status: WorkOrderStatus;
  /** Called with a status other than the current one; picking the current one just closes. */
  onSelect: (status: WorkOrderStatus) => void;
  /** Phone width: a smaller pill, as the page title uses there. */
  compact?: boolean;
  disabled?: boolean;
}

const ALL_STATUSES = STATUS_GROUPS.flatMap((group) => [...group.statuses]);

/**
 * The status in the page title, made into the control that changes it.
 *
 * It keeps looking like the status — same colour, same size — and gains only a chevron, because the
 * title is where the manager already reads it. A menu rather than a native `<select>`: each entry
 * carries its own colour dot, so the choice is made by recognising the status the way the rest of
 * the app shows it, not by reading nine near-identical labels.
 *
 * Keyboard: Enter/Space/ArrowDown open it on the current status; arrows, Home and End move; Escape
 * closes and returns focus to the trigger; Tab closes. The current status is checked and
 * `aria-checked`, so a screen reader hears it, not just sees a tick.
 */
export const WorkOrderStatusMenu: FC<Props> = ({ status, onSelect, compact = false, disabled }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Map<WorkOrderStatus, HTMLButtonElement>>(new Map());
  const menuId = useId();

  const color = getWorkOrderStatusColor(status);
  const label = getWorkOrderStatusLabel(status);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  useOutsideClick(rootRef, () => setOpen(false), open);

  // Focus lands on the current status when the menu opens, so one keystroke reaches its neighbours.
  useEffect(() => {
    if (open) itemRefs.current.get(status)?.focus();
  }, [open, status]);

  const focusAt = (index: number) => {
    const next = ALL_STATUSES[(index + ALL_STATUSES.length) % ALL_STATUSES.length];
    itemRefs.current.get(next)?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = ALL_STATUSES.findIndex((s) => itemRefs.current.get(s) === document.activeElement);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        focusAt(current + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusAt(current - 1);
        break;
      case "Home":
        event.preventDefault();
        focusAt(0);
        break;
      case "End":
        event.preventDefault();
        focusAt(ALL_STATUSES.length - 1);
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      case "Tab":
        setOpen(false);
        break;
    }
  };

  const choose = (next: WorkOrderStatus) => {
    close(true);
    if (next !== status) onSelect(next);
  };

  return (
    // Heading size: sit on the title's baseline, so "Geplant" reads as part of the same line.
    // `align-middle` would centre this 44px box on the x-height instead and drop the text below it.
    // Phone: a small pill, centred, as the static badge beside the title always was.
    <div ref={rootRef} className={`relative inline-block ${compact ? "align-middle" : "align-baseline"}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Status: ${label}. Status ändern`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        // A pill with a tinted fill and a border in the status colour: at heading size, coloured
        // text alone reads as a label, and nothing about a chevron that small says "press me".
        style={{
          color,
          backgroundColor: `${color}1A`,
          borderColor: `${color}66`,
        }}
        className={`group relative inline-flex min-h-[44px] cursor-pointer items-center rounded-full border-2 transition-shadow duration-150 hover:shadow-[0_2px_10px_-2px_rgba(15,23,42,0.3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#124093] disabled:cursor-not-allowed disabled:opacity-60 ${
          compact ? "gap-1 pl-3 pr-2" : "gap-2 pl-4 pr-3"
        }`}
      >
        <span
          className={
            compact ? "text-sm font-medium leading-none" : "text-[32px] leading-none max-[1080px]:text-[24px]"
          }
        >
          {label}
        </span>
        <ChevronDown
          size={compact ? 18 : 26}
          strokeWidth={3}
          aria-hidden="true"
          className={`shrink-0 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Status des Auftrags"
          onKeyDown={onMenuKeyDown}
          className="absolute left-0 top-full z-30 mt-2 w-[min(18rem,calc(100vw-32px))] rounded-2xl border border-slate-200 bg-white py-2 text-base font-normal text-slate-900 shadow-[0_8px_24px_-4px_rgba(15,23,42,0.18)]"
        >
          {STATUS_GROUPS.map((group, groupIndex) => (
            <div
              key={group.label}
              role="group"
              aria-label={group.label}
              className={groupIndex > 0 ? "mt-1 border-t border-slate-100 pt-1" : ""}
            >
              <p
                aria-hidden="true"
                className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                {group.label}
              </p>
              {group.statuses.map((value) => {
                const selected = value === status;
                return (
                  <button
                    key={value}
                    ref={(node) => {
                      if (node) itemRefs.current.set(value, node);
                      else itemRefs.current.delete(value);
                    }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => choose(value)}
                    className="flex min-h-[44px] w-full items-center gap-3 px-4 text-left transition-colors duration-150 hover:bg-slate-50 focus-visible:bg-slate-100 focus-visible:outline-none"
                  >
                    <span
                      aria-hidden="true"
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: getWorkOrderStatusColor(value) }}
                    />
                    <span className={`flex-1 ${selected ? "font-semibold" : ""}`}>
                      {getWorkOrderStatusLabel(value)}
                    </span>
                    {selected && <Check size={18} aria-hidden="true" className="shrink-0 text-[#124093]" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
